// NavCollapse.jsx
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import NavItem from '../NavItem';
import Transitions from '../../../../components/extended/Transitions';
import useConfig from '../../../../hooks/useConfig';
import { useGetMenuMaster } from '../../../../api/menu';
import { IconChevronDown, IconChevronRight, IconChevronUp } from '@tabler/icons-react';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export default function NavCollapse({ menu, level = 1 }) {
  const ref = useRef(null);
  const { borderRadius } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoverStatus, setHover] = useState(false);

  const isSelected = selected === menu.id;

  useEffect(() => {
    const compareSize = () => {
      if (ref.current) setHover(ref.current.scrollWidth > ref.current.clientWidth);
    };
    compareSize();
    window.addEventListener('resize', compareSize);
    return () => window.removeEventListener('resize', compareSize);
  }, []);

  useEffect(() => {
    let found = false;
    const checkActive = (items) => {
      items.forEach((item) => {
        if (item.url === pathname || (item.link && matchPath({ path: item.link, end: false }, pathname))) {
          found = true;
          setSelected(menu.id);
          setOpen(true);
        }
        if (item.children) checkActive(item.children);
      });
    };
    if (menu.children) checkActive(menu.children);
    if (!found) {
      setSelected(null);
      setOpen(false);
    }
  }, [pathname, menu]);

  const handleClickMini = (event) => {
    if (drawerOpen) {
      setOpen(!open);
      setSelected(open ? null : menu.id);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClosePopper = () => {
    setAnchorEl(null);
  };

  const menus = menu.children?.map((item, idx) => {
    const delay = drawerOpen ? `${idx * 30}ms` : '0ms';
    switch (item.type) {
      case 'collapse':
        return (
          <div key={item.id} style={{ animation: `slideIn 0.25s ease-out ${delay} both` }}>
            <NavCollapse menu={item} level={level + 1} />
          </div>
        );
      case 'item':
        return (
          <div key={item.id} style={{ animation: `slideIn 0.25s ease-out ${delay} both` }}>
            <NavItem item={item} level={level + 1} />
          </div>
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const Icon = menu.icon;
  const menuIcon = Icon ? (
    <Icon 
      stroke={1.5} 
      size={drawerOpen ? '16px' : '18px'} 
      style={{ 
        color: isSelected ? '#FFFFFF' : '#95A5A6',
        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
      }} 
    />
  ) : (
    <FiberManualRecordIcon
      sx={{ 
        width: isSelected ? 7 : 5, 
        height: isSelected ? 7 : 5,
        color: isSelected ? '#FFFFFF !important' : '#95A5A6 !important',
        transition: 'color 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1), height 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    />
  );

  const chevronColor = isSelected ? '#FFFFFF' : '#95A5A6';

  return (
    <>
      <ListItemButton
        onClick={handleClickMini}
        onMouseEnter={!drawerOpen ? handleClickMini : undefined}
        onMouseLeave={!drawerOpen ? handleClosePopper : undefined}
        sx={{
          position: 'relative',
          borderRadius: `${borderRadius}px`,
          mb: '2px',
          py: '4px',
          pl: drawerOpen ? (level === 1 ? '12px' : `${12 + level * 8}px`) : '7px',
          pr: '7px',
          color: isSelected ? '#FFFFFF !important' : '#2C3E50 !important',
          backgroundColor: isSelected ? '#3498DB !important' : 'transparent',
          borderLeft: isSelected ? '3px solid #3498DB' : '3px solid transparent',
          transition: 'background-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-left-color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: isSelected ? 'pulseGlow 2s infinite' : 'none',
          '&:hover': {
            backgroundColor: '#2980B9 !important',
            color: '#FFFFFF !important',
            '& .menu-icon': { transform: 'scale(1.1)' }
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: '#3498DB !important',
            color: '#FFFFFF !important'
          }
        }}
      >
        <ListItemIcon
          className="menu-icon"
          sx={{
            minWidth: level === 1 ? '28px' : '20px',
            color: isSelected ? '#FFFFFF !important' : '#95A5A6 !important',
            display: 'flex',
            justifyContent: 'center',
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {menuIcon}
        </ListItemIcon>

        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <Tooltip title={menu.title} disableHoverListener={!hoverStatus}>
            <ListItemText
              primary={
                <Typography
                  ref={ref}
                  noWrap
                  variant="body2"
                  fontWeight={isSelected ? 600 : 500}
                  sx={{ 
                    color: 'inherit !important',
                    fontSize: '0.85rem'
                  }}
                >
                  {menu.title}
                </Typography>
              }
              secondary={
                menu.caption && (
                  <Typography variant="caption" sx={{ color: '#7F8C8D !important', fontSize: '0.6rem', mt: '1px' }}>
                    {menu.caption}
                  </Typography>
                )
              }
            />
          </Tooltip>
        )}

        {drawerOpen && (
          <ListItemIcon
            sx={{
              minWidth: '18px',
              display: 'flex',
              justifyContent: 'center',
              color: chevronColor + ' !important',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <IconChevronDown stroke={1.5} size="12px" />
          </ListItemIcon>
        )}
      </ListItemButton>

      {!drawerOpen && (
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="right-start"
          modifiers={[{ name: 'offset', options: { offset: [-8, 0] } }]}
          sx={{
            zIndex: 2001,
            minWidth: 140,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -4,
              top: '50%',
              transform: 'translateY(-50%) rotate(45deg)',
              width: 8,
              height: 8,
              bgcolor: 'background.paper',
              border: '1px solid #DDD'
            }
          }}
        >
          <Transitions in={Boolean(anchorEl)} type="slide" direction="right">
            <Paper
              sx={{
                mt: 0.8,
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                borderRadius: 1,
                border: '1px solid #DDD'
              }}
            >
              <ClickAwayListener onClickAway={handleClosePopper}>
                <Box sx={{ p: 0.3 }}>{menus}</Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        </Popper>
      )}

      {drawerOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            disablePadding
            sx={{
              position: 'relative',
              pl: level === 1 ? '18px' : '12px',
              '&:before': {
                content: '""',
                position: 'absolute',
                left: level === 1 ? '9px' : '6px',
                top: 0,
                bottom: 0,
                width: '2px',
                background: 'linear-gradient(to bottom, #3498DB, #2980B9)'
              }
            }}
          >
            {menus}
          </List>
        </Collapse>
      )}

      <style jsx global>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulseGlow {
          0% { box-shadow: 0 0 0 0 rgba(52, 152, 219, 0.4); }
          70% { box-shadow: 0 0 0 8px rgba(52, 152, 219, 0); }
          100% { box-shadow: 0 0 0 0 rgba(52, 152, 219, 0); }
        }
      `}</style>
    </>
  );
}

NavCollapse.propTypes = {
  menu: PropTypes.any.isRequired,
  level: PropTypes.number,
  parentId: PropTypes.string
};